import { useParams, useSearch } from "wouter";
import { useEffect } from "react";
import {
  useGetVenue,
  useListTournaments,
  useGetTournamentResults,
  getGetVenueQueryKey,
  getListTournamentsQueryKey,
  getGetTournamentResultsQueryKey,
} from "@workspace/api-client-react";
import { formatDateTime, formatMoney } from "@/lib/format";
import { Loader2, MapPin, Calendar, Trophy } from "lucide-react";
import logoUrl from "@/assets/poker-logo.png";

type EmbedType = "tournaments" | "results";

function useEmbedType(): EmbedType {
  const search = useSearch();
  const params = new URLSearchParams(search);
  const t = params.get("type");
  return t === "results" ? "results" : "tournaments";
}

export default function VenueEmbed() {
  const { id } = useParams<{ id: string }>();
  const venueId = Number(id);
  const type = useEmbedType();

  // Make iframe-friendly: transparent background so the host page shows through if desired.
  useEffect(() => {
    document.documentElement.classList.add("dark");
    document.body.style.background = "transparent";
    return () => { document.body.style.background = ""; };
  }, []);

  const { data: venue, isLoading: venueLoading } = useGetVenue(venueId, {
    query: { enabled: !!venueId, queryKey: getGetVenueQueryKey(venueId) },
  });

  if (venueLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (!venue) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground p-8 text-sm">
        Venue not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground p-4 sm:p-6">
      <header className="flex items-center gap-3 pb-4 mb-4 border-b border-border/60">
        <img src={logoUrl} alt="Interclub Poker Circuit" className="h-10 w-auto" />
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wide text-primary font-semibold">Interclub Poker Circuit</div>
          <div className="flex items-center gap-1.5 text-sm text-foreground font-semibold truncate">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            {venue.name}
            {venue.city && <span className="text-muted-foreground font-normal">· {venue.city}</span>}
          </div>
        </div>
      </header>

      {type === "tournaments" ? <UpcomingTournaments venueId={venueId} /> : <RecentResults venueId={venueId} />}

      <footer className="mt-6 pt-3 border-t border-border/60 text-[11px] text-muted-foreground text-center">
        Powered by <a href="/" target="_blank" rel="noopener" className="text-primary hover:underline">Interclub Poker Circuit</a>
      </footer>
    </div>
  );
}

function UpcomingTournaments({ venueId }: { venueId: number }) {
  const q = { venueId, status: "upcoming" as const, limit: 200 };
  const { data, isLoading } = useListTournaments(
    q,
    { query: { queryKey: getListTournamentsQueryKey(q) } },
  );

  if (isLoading) return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const sorted = [...(data?.items ?? [])].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (sorted.length === 0) {
    return <div className="py-10 text-center text-sm text-muted-foreground">No upcoming tournaments scheduled.</div>;
  }

  return (
    <div className="space-y-2">
      <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5 mb-2">
        <Calendar className="h-4 w-4 text-primary" /> Upcoming Tournaments
      </h2>
      <ul className="space-y-2">
        {sorted.map(t => (
          <li key={t.id} className="rounded-md border border-border/60 bg-card/60 px-3 py-2.5 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-medium text-foreground truncate text-sm">{t.name}</div>
              <div className="text-xs text-muted-foreground">{formatDateTime(t.date)}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs text-muted-foreground">Buy-in</div>
              <div className="text-sm font-semibold text-primary">{formatMoney(t.buyIn)}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecentResults({ venueId }: { venueId: number }) {
  const q = { venueId, status: "completed" as const, limit: 200 };
  const { data: tournamentsPage, isLoading } = useListTournaments(
    q,
    { query: { queryKey: getListTournamentsQueryKey(q) } },
  );

  if (isLoading) return <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  const recent = [...(tournamentsPage?.items ?? [])]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 3);

  if (recent.length === 0) {
    return <div className="py-10 text-center text-sm text-muted-foreground">No recent results yet.</div>;
  }

  return (
    <div className="space-y-4">
      <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5 mb-1">
        <Trophy className="h-4 w-4 text-primary" /> Recent Results
      </h2>
      {recent.map(t => (
        <ResultsBlock key={t.id} tournamentId={t.id} name={t.name} date={t.date} />
      ))}
    </div>
  );
}

function ResultsBlock({ tournamentId, name, date }: { tournamentId: number; name: string; date: string }) {
  const { data, isLoading } = useGetTournamentResults(tournamentId, {
    query: { queryKey: getGetTournamentResultsQueryKey(tournamentId) },
  });

  const top = [...(data ?? [])].sort((a, b) => a.position - b.position).slice(0, 3);

  return (
    <div className="rounded-md border border-border/60 bg-card/60 px-3 py-2.5">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="font-medium text-foreground text-sm truncate">{name}</div>
        <div className="text-[11px] text-muted-foreground shrink-0">{formatDateTime(date)}</div>
      </div>
      {isLoading ? (
        <div className="text-xs text-muted-foreground">Loading…</div>
      ) : top.length === 0 ? (
        <div className="text-xs text-muted-foreground">No results recorded.</div>
      ) : (
        <ol className="space-y-1">
          {top.map(r => (
            <li key={r.id} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${
                  r.position === 1 ? "bg-primary/20 text-primary" :
                  r.position === 2 ? "bg-muted text-foreground" :
                  "bg-muted/60 text-muted-foreground"
                }`}>{r.position}</span>
                <span className="text-foreground truncate">{r.playerName}</span>
              </span>
              <span className="text-primary font-semibold">{formatMoney(r.winnings)}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
